import './qrCode.scss';

import React, { FunctionComponent, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import ColorPicker from '../ColorPicker/ColorPicker';
import { DonateButtonFormData } from '../../../types/givingCheckoutForm';
import { QRCode as QRCodeImage } from 'react-qrcode-logo';
import { WarningMessage } from '../WarningMessage/WarningMessage';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

type QRCodeProps = { link: string; centreImage?: boolean };

const QRCode: FunctionComponent<QRCodeProps> = ({
  link,
  centreImage = false,
}) => {
  const { t } = useTranslation();
  const { register } = useFormContext<DonateButtonFormData>();
  const [
    qrCodeType,
    qrCodeBackgroundColor,
    qrCodeForegroundColor,
    qrCodeEyeColor,
    qrCodeIncludeJgLogo,
  ] = useWatch<
    DonateButtonFormData,
    [
      'qrCodeType',
      'qrCodeBackgroundColor',
      'qrCodeForegroundColor',
      'qrCodeEyeColor',
      'qrCodeIncludeJgLogo',
    ]
  >({
    name: [
      'qrCodeType',
      'qrCodeBackgroundColor',
      'qrCodeForegroundColor',
      'qrCodeEyeColor',
      'qrCodeIncludeJgLogo',
    ],
  });
  const qrCodeRef = useRef<QRCodeImage>(null);

  const colorPickerDisabled = qrCodeType === 'standard';
  const defaultBgColour = '#ffffff';
  const defaultFgColour = '#000000';
  const defaultEyeColour = '#000000';

  const logoImageSvgString = `data:image/svg+xml,%3Csvg%20width%3D%2272%22%20height%3D%2272%22%20viewBox%3D%220%200%2072%2072%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M71.2349%2028.2462H46.0751L31.3729%2042.8923H51.5499C48.9048%2048.8%2042.9993%2052.9231%2036.1096%2052.9231C26.7592%2052.9231%2019.1928%2045.3538%2019.1928%2036C19.1928%2026.6462%2026.7592%2019.0769%2036.1096%2019.0769C39.4929%2019.0769%2042.6302%2020.0615%2045.2754%2021.7846L58.8703%208.18462C52.6572%203.07692%2044.7217%200%2035.9865%200C16.117%200%200%2016.1231%200%2036C0%2055.8769%2016.117%2072%2035.9865%2072C55.856%2072%2071.9731%2055.8769%2071.9731%2036C72.0961%2033.3538%2071.7885%2030.7077%2071.2349%2028.2462Z%22%20fill%3D%22%23${
    qrCodeType === 'standard' ? '000' : qrCodeEyeColor.replace('#', '')
  }%22%2F%3E%0A%3C%2Fsvg%3E%0A`;

  const onClickDownloadButton = () => {
    qrCodeRef.current?.download('png', 'Giving Checkout QR Code');
  };

  return (
    <>
      <p className="jg-color--empress jg-font-weight-regular">
        {t('linkDetails.qrcode.description')}
      </p>

      <fieldset className="jg-form-group jg-space-mbsm jg-space-mtmd">
        <legend className="jg-form-label jg-space-mbxs jg-color--nero">
          {t('linkDetails.qrcode.chooseCodeType')}
        </legend>
        <input
          className="jg-radio"
          type="radio"
          id="qrCodeType-standard"
          value="standard"
          {...register('qrCodeType')}
        />
        <label className="jg-form-label" htmlFor="qrCodeType-standard">
          {t('linkDetails.qrcode.standardQrCode')}
        </label>
        <input
          className="jg-radio"
          type="radio"
          id="qrCodeType-colour"
          value="colour"
          {...register('qrCodeType')}
        />
        <label className="jg-form-label" htmlFor="qrCodeType-colour">
          {t('linkDetails.qrcode.colourQrCode')}
        </label>
      </fieldset>

      {/* class `qrcode` used for centering from parent  */}
      <div
        className={classNames('jg-space-mbms', { qrCodeCentre: centreImage })}
        aria-hidden
        data-qa="qr-code-wrapper"
      >
        <QRCodeImage
          ref={qrCodeRef}
          value={link}
          size={230}
          bgColor={
            qrCodeType === 'standard' ? defaultBgColour : qrCodeBackgroundColor
          }
          fgColor={
            qrCodeType === 'standard' ? defaultFgColour : qrCodeForegroundColor
          }
          eyeColor={
            qrCodeType === 'standard' ? defaultEyeColour : qrCodeEyeColor
          }
          logoImage={qrCodeIncludeJgLogo ? logoImageSvgString : undefined}
          logoOpacity={1}
          removeQrCodeBehindLogo={true}
          logoPadding={10}
          logoPaddingStyle="circle"
        />
      </div>

      <WarningMessage message={t('linkDetails.qrcode.contrastWarning')} />

      <ColorPicker
        name="qrCodeBackgroundColor"
        id="qrCodeBackgroundColor"
        label={t('linkDetails.qrcode.backgroundColour')}
        displayHeadingToRight
        disabled={colorPickerDisabled}
        defaultColour={defaultBgColour}
      />
      <ColorPicker
        name="qrCodeForegroundColor"
        id="qrCodeForegroundColor"
        label={t('linkDetails.qrcode.foregroundColour')}
        displayHeadingToRight
        disabled={colorPickerDisabled}
        defaultColour={defaultFgColour}
      />
      <ColorPicker
        name="qrCodeEyeColor"
        id="qrCodeEyeColor"
        label={t('linkDetails.qrcode.eyeColour')}
        displayHeadingToRight
        disabled={colorPickerDisabled}
        defaultColour={defaultEyeColour}
      />

      <fieldset className="jg-form-group">
        <input
          type="checkbox"
          className="jg-checkbox"
          id="qrCodeIncludeJgLogo"
          {...register('qrCodeIncludeJgLogo')}
        />
        <label htmlFor="qrCodeIncludeJgLogo" className="jg-form-label">
          {t('linkDetails.qrcode.includeJgLogo')}
        </label>
      </fieldset>

      <button
        className="jg-btn jg-btn--secondary gc-button-width"
        onClick={onClickDownloadButton}
      >
        {t('linkDetails.qrcode.downloadButtonLabel')}
      </button>
    </>
  );
};

export default QRCode;
